import type {
  CardPosition,
  DealCardsAction,
  GameState,
  RoleCard,
  RoleId,
} from "../types";
import { shuffleRoles } from "../utils/rng";
import {
  buildAnnouncementTimeline,
  buildNightRoleQueue,
} from "../utils/role-rules";

function makeCards(roleIds: readonly string[]): readonly RoleCard[] {
  return roleIds.map((roleId, index) => ({
    id: `card-${String(index + 1).padStart(3, "0")}`,
    roleId: roleId as RoleCard["roleId"],
  }));
}

function buildPositions(
  state: GameState,
  cards: readonly RoleCard[],
): {
  readonly cardPositions: Readonly<Record<string, CardPosition>>;
  readonly players: GameState["players"];
  readonly centerSlots: GameState["centerSlots"];
} {
  const cardPositions: Record<string, CardPosition> = {};
  const players = state.players.map((player, index) => {
    const card = cards[index];
    if (!card) return player;
    cardPositions[card.id] = { zone: "player", playerId: player.id };
    return {
      ...player,
      startingCardId: card.id,
    };
  });

  const centerCards = cards.slice(
    state.players.length,
    state.players.length + 3,
  );
  const centerSlots = centerCards.map((card, index) => {
    cardPositions[card.id] = {
      zone: "center",
      slotIndex: index as 0 | 1 | 2,
    };
    return card.id;
  }) as [string, string, string];

  return { cardPositions, players, centerSlots };
}

function buildActorPlayerIdsByRole(
  players: GameState["players"],
  cards: readonly RoleCard[],
): ReadonlyMap<RoleId, readonly string[]> {
  const cardById = new Map(cards.map((card) => [card.id, card]));
  const actorPlayerIdsByRole = new Map<RoleId, string[]>();

  for (const player of players) {
    if (!player.startingCardId) continue;
    const card = cardById.get(player.startingCardId);
    if (!card) continue;
    const actorPlayerIds = actorPlayerIdsByRole.get(card.roleId) ?? [];
    actorPlayerIdsByRole.set(card.roleId, [...actorPlayerIds, player.id]);
  }

  return actorPlayerIdsByRole;
}

export function applyDealCards(
  state: GameState,
  _action: DealCardsAction,
): GameState {
  const shuffledRoleIds = shuffleRoles(state.selectedRoleIds, state.seed);
  const cards = makeCards(shuffledRoleIds);
  const { cardPositions, players, centerSlots } = buildPositions(state, cards);
  const actorPlayerIdsByRole = buildActorPlayerIdsByRole(players, cards);

  return {
    ...state,
    version: state.version + 1,
    phase: "role-peek",
    players,
    cards,
    cardPositions,
    centerSlots,
    rolePeekConfirmedPlayerIds: [],
    nightRoleQueue: buildNightRoleQueue(
      state.selectedRoleIds,
      actorPlayerIdsByRole,
    ),
    announcerTimeline: buildAnnouncementTimeline(state.selectedRoleIds),
  };
}
